import { resolveUiV4 } from '@/lib/uiFlag';
// SE1 (7.3b): remaining antd sider icons converted to their Solar equivalents
// — resources.tsx is shared shell core and must stay antd-free.
import { useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers/useCurrentUser';
import {
  BellLinear,
  BookLinear,
  BranchingPathsUpLinear,
  BuildingsLinear,
  CalendarLinear,
  Chart2Linear,
  ChartLinear,
  ChatRoundLinear,
  CloudDownloadLinear,
  DocumentTextLinear,
  DollarLinear,
  DownloadSquareLinear,
  ExitLinear,
  GlobusLinear,
  GraphUpLinear,
  HomeAngle2Linear,
  LibraryLinear,
  ListLinear,
  NotebookLinear,
  QuestionCircleLinear,
  SettingsLinear,
  SettingsMinimalisticLinear,
  ShareOutline,
  Tuning2Linear,
  UsersGroupRoundedLinear,
  VolumeLoudLinear,
} from '@assessprep/icons/solar';
import { useMemo } from 'react';

type Translate = (key: string, options?: any) => string;

// APL-11850: extracted verbatim from src/App.tsx so the Refine `resources`
// list is a standalone module both shells (AppV1 / App) can consume.
// `translate` is passed in from the caller's i18nProvider so memoization
// behavior matches the original App.tsx exactly.
export const useAppResources = (translate: Translate) => {
  const currentUser = useCurrentUser();
  const enabledFeatures = useEnabledFeatures();
  const isRoomManagementEnabled = enabledFeatures?.enable_room_management;
  const isCommonAssessmentEnabled = enabledFeatures?.separate_common_assessment_tab;
  const isDiagnosticToolAllowed = enabledFeatures?.allow_diagnostic_tool;
  const currentRole = currentUser?.role;
  const currentOrgType = currentUser?.org?.org_type;
  const notDistrictQbAuthor = currentOrgType !== 'district' && currentRole === 'qb_author';

  // Memoize resources array to re-render when currentUser changes
  // !IMPORTANT: this is using name.list permission by default to show the item in the sider menu but can use other actions also by passing like in org settings
  const resources = useMemo(
    () => [
      {
        name: 'orgs',
        list: '/orgs',
        create: '/orgs/create',
        edit: '/orgs/edit/:id',
        show: '/orgs/:orgId/settings',
        meta: {
          label:
            currentRole === 'support'
              ? translate('CommonText.schools')
              : translate('CommonText.orgs'),
          icon: <BuildingsLinear />,
          // parent: currentRole === 'support' ? '' : 'superadmin_settings',
        },
      },
      {
        name: 'overviews',
        list: '/overview',
        meta: {
          label: translate('CommonText.overview'),
          // icon: <HomeOutlined />,
          icon: <HomeAngle2Linear />,
        },
      },
      {
        name: 'experiences',
        list: '/assessments',
        create: '/assessments/create',
        edit: '/assessments/edit/:id',
        meta: {
          label:
            currentRole === 'parent'
              ? translate('LibraryPage.assessments')
              : translate('CommonText.my_assessments'),
          icon: <DocumentTextLinear />,
        },
        // show: "/experience/show/:id",
      },
      {
        name: 'librarys',
        list: notDistrictQbAuthor ? '/library/tests' : '/library/explore',
        //passed isentifier to make sider menu item active when on /library/tests
        identifier: 'library/tests',
        meta: { label: translate('CommonText.library'), icon: <LibraryLinear /> },
      },
      {
        name: 'common_assessments',
        list: '/common_assessments/explore',
        meta: {
          label: translate('CommonText.common_assessments'),
          icon: <ShareOutline />,
        },
        options: {
          hide:
            !isCommonAssessmentEnabled ||
            (currentRole === 'admin' && currentOrgType === 'district'),
        },
      },
      {
        // LD1: legacy `gradebooks` entry deleted (gradebook_v3 GA); visibility
        // still gated by the `gradebook_v3` check in resourcePermissions.
        name: 'gradebook-v3',
        list: '/gradebook-v3',
        meta: { label: translate('CommonText.gradebook'), icon: <ChartLinear /> },
      },
      {
        // APL-11550 (GW1-growth): resource id `student_growths` (BE permission
        // `student_growth` pluralized); the route path stays `/student-growth`.
        // District QB author + `student_growth` flag gating lives in
        // resourcePermissions. v4-only route → hidden in the v1 shell.
        name: 'student_growths',
        list: '/student-growth',
        meta: {
          label: translate('student_growth.nav_label'),
          icon: <GraphUpLinear />,
        },
        options: {
          hide: !resolveUiV4(),
        },
      },
      {
        name: 'users',
        list: '/users',
        create: '/users/create',
        edit: '/users/edit/:id',
        show: '/users/show/:id',
        meta: {
          label: translate('CommonText.users'),
          icon: <UsersGroupRoundedLinear />,
        },
      },
      {
        name: 'groups',
        list: '/groups',
        create: '/groups/create',
        edit: '/groups/edit/:id',
        show: '/groups/show/:id',
        meta: { label: translate('CommonText.classes'), icon: <NotebookLinear /> },
      },
      {
        name: 'schools',
        list: '/schools',
        meta: {
          label: translate('CommonText.schools'),
          icon: <BuildingsLinear />,
        },
      },
      {
        name: 'rooms',
        list: '/rooms',
        create: '/rooms/create',
        edit: '/rooms/edit/:id',
        show: '/rooms/show/:id',
        meta: {
          label: translate('CommonText.rooms'),
          icon: <ExitLinear />,
        },
        options: {
          hide: !isRoomManagementEnabled,
        },
      },
      {
        name: 'org_settings',
        list: '/settings/programs',
        show: '/settings/programs',
        meta: {
          label: translate('CommonText.settings'),
          icon: <SettingsMinimalisticLinear />,
          // action: 'settings', // check this permission instead of default list to show the item in the sider menu = not using - 2 orgs as names in resources causing issues so using org_settings as name
        },
      },
      {
        name: 'superadmin_settings',
        list: '/settings',
        meta: {
          label: translate('CommonText.superadmin_more_settings'),
          icon: <SettingsLinear />,
        },
      },
      {
        name: 'features',
        list: '/features',
        create: '/features/create',
        edit: '/features/edit/:id',
        show: '/features/show/:id',
        meta: {
          label: translate('CommonText.features'),
          icon: <DollarLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'app_releases',
        list: '/app-releases',
        create: '/app-releases/create',
        edit: '/app-releases/edit/:id',
        show: '/app-releases/show/:id',
        meta: {
          label: translate('CommonText.app_releases'),
          icon: <CloudDownloadLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'announcements',
        list: '/announcements',
        create: '/announcements/create',
        edit: '/announcements/edit/:id',
        meta: {
          label: translate('Announcements.announcements'),
          icon: <VolumeLoudLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'plans',
        list: '/plans',
        create: '/plans/create',
        edit: '/plans/edit/:id',
        show: '/plans/show/:id',
        meta: {
          label: translate('CommonText.plans'),
          icon: <DollarLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'questionbanks',
        list: '/questionbanks',
        create: '/questionbanks/create',
        edit: '/questionbanks/edit/:id',
        meta: {
          label: translate('CommonText.questionbanks'),
          icon: <ListLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'feedbacks',
        list: '/feedbacks',
        show: '/feedbacks/show/:id',
        meta: {
          label: translate('CommonText.feedback'),
          icon: <ChatRoundLinear />,
          parent: currentRole === 'support' ? '' : 'superadmin_settings',
        },
      },
      {
        name: 'curriculum',
        list: '/curriculum',
        show: '/curriculum/:id',
        meta: {
          label: translate('CommonText.curriculum'),
          icon: <BookLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'rubrics',
        list: '/rubrics',
        create: '/rubrics/create',
        edit: '/rubrics/edit/:id',
        meta: {
          label: translate('CommonText.rubrics'),
          icon: <ListLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'integrations',
        list: '/integrations',
        create: '/integrations/create',
        edit: '/integrations/edit/:id',
        meta: {
          icon: <ListLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'prompts',
        list: '/prompts',
        create: '/prompts/create',
        edit: '/prompts/edit/:id',
        meta: {
          label: translate('CommonText.prompts_dashboard'),
          parent: 'superadmin_settings',
          icon: <SettingsLinear />,
        },
      },
      {
        name: 'boards',
        list: '/boards',
        create: '/boards/create',
        edit: '/boards/edit/:id',
        show: '/boards/show/:id',
        meta: {
          label: translate('CommonText.boards'),
          icon: <ListLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'tags',
        list: '/tags',
        create: '/tags/create',
        edit: '/tags/edit/:id',
        show: '/tags/show/:id',
        meta: {
          label: translate('CommonText.tags'),
          icon: <ListLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'standard_sets',
        list: '/standard-sets',
        create: '/standard-sets/create',
        edit: '/standard-sets/:id',
        show: '/standard-sets/:id',
        meta: {
          label: translate('CommonText.standard_sets'),
          icon: <ListLinear />,
          // Show in main menu for non-district qb_author, otherwise under superadmin_settings
          parent: notDistrictQbAuthor ? undefined : 'superadmin_settings',
        },
      },
      {
        name: 'programmes',
        list: '/programmes',
        create: '/programmes/create',
        edit: '/programmes/edit/:id',
        show: '/programmes/show/:id',
        meta: {
          label: translate('CommonText.programmes'),
          icon: <ListLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'resource_sheets',
        list: '/resource-sheets',
        meta: {
          label: translate('CommonText.resource_sheet'),
          icon: <ListLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'global_ai_usage',
        list: '/ai-usage',
        meta: {
          label: translate('GlobalAIUsage.ai_usage'),
          icon: <Chart2Linear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'live_world_map',
        list: '/live-map',
        meta: {
          label: translate('LiveWorldMap.live_map'),
          icon: <GlobusLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'assessments_calendar',
        list: '/assessments-calendar',
        meta: {
          label: translate('AssessmentsCalendar.menu_title'),
          icon: <CalendarLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'subject_merger',
        list: '/admin/subject-merger',
        meta: {
          label: translate('SubjectMerger.menu_title'),
          icon: <BranchingPathsUpLinear />,
          parent: 'superadmin_settings',
        },
      },
      {
        name: 'whats_new',
        list: '/whats-new',
        meta: {
          label: translate('Announcements.whats_new'),
          icon: <BellLinear />,
        },
        options: {
          hide: true,
        },
      },

      // ONLY add here if we want to show in the side menu
      // {
      //   name: 'embedded-resources',
      //   url: '/embed/:uuid',
      //   title: 'Embedded Resources',
      //   resourceName: 'embeddedResources',
      //   crudConfig: {},
      //   singleResourceName: 'embed_resource',
      //   meta: {
      //     label: translate('CommonText.embedded_resources'),
      //     icon: <SettingsLinear />,
      //   },
      //   options: {
      //     hide: true, // Hide from navigation since it's accessed via direct URL
      //   },
      // },
      {
        // APL-12040 (dev call, 2 Aug): the sidebar Help entry is STUDENT-ONLY
        // and leaves the app for the Zendesk For-Students category.
        // Admin/teacher reach help via UserMenu's help-center instead;
        // qb_author, support and superadmin have none.
        // PARENTS ARE DELIBERATELY EXCLUDED (dev call, second pass): the entry
        // was briefly widened to parents on the reasoning that UserMenu's
        // help-center is admin/teacher-only, so they would otherwise have no
        // help affordance at all — dev reviewed that and chose to keep them
        // out. So "parents have no help entry point" is the intended state,
        // not an oversight; don't re-add them without asking.
        // The internal /help route is untouched — only this nav entry changes.
        name: 'helps',
        list: '/help',
        meta: {
          label: translate('CommonText.help'),
          icon: <QuestionCircleLinear />,
          externalUrl:
            'https://assessprep.zendesk.com/hc/en-us/categories/32413280155165-For-Students',
        },
        options: {
          hide: currentRole !== 'student',
        },
      },
      {
        name: 'download_apps',
        list: '/download-apps',
        meta: {
          label: translate('CommonText.download_apps'),
          icon: <DownloadSquareLinear />,
        },
      },
      {
        name: 'diagnostic_tools',
        list: '/diagnostic-tools',
        meta: {
          label: translate('OfflineApp.diagnostic_tool'),
          icon: <Tuning2Linear />,
        },
        options: {
          hide: !isDiagnosticToolAllowed || currentRole !== 'student',
        },
      },
      {
        name: 'unsupported',
        list: '/unsupported',
        meta: {
          label: translate('CommonText.unsupported_version_notice'),
          icon: <SettingsLinear />,
        },
      },
    ],
    [
      notDistrictQbAuthor,
      translate,
      isRoomManagementEnabled,
      isCommonAssessmentEnabled,
      isDiagnosticToolAllowed,
      currentRole,
      currentOrgType,
    ]
  );

  return resources;
};
